import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import RequestState from 'Enums/RequestState'
import en from 'javascript-time-ago/locale/en'
import TimeAgo from 'javascript-time-ago'
import CardActions from 'Stores/Card/Actions'
import CardSelectors from 'Stores/Card/Selectors'
import { Virtuoso } from 'react-virtuoso'
import Styles from './CardSessionsList.module.css'
import CardSessionListItem from 'Components/CardSessionListItem/CardSessionListItem'
import { handleMaxPower } from 'Utils/ConnectorFunctions'
import AuthSelectors from 'Stores/Auth/Selectors'
import { Emitter } from 'Services/EmitterService'
import CardSessionListItemSkeleton from 'Components/CardSessionListItem/CardSessionListItemSkeleton'
import { useOverlayScrollbars } from 'overlayscrollbars-react'
import { IFText } from 'Components'
import { useTranslation } from 'react-i18next'

TimeAgo.addDefaultLocale(en)

const CardSessionsList = ({
  cardSessions = [],
  selectedCard,
  fetchCardSessionList,
  fetchCardSessionListRequestState,
  fetchCardListRequestState,
  fetchCardRequestState,
  operator,
  emitterUpdateCardSessionStatus,
}) => {
  const { t } = useTranslation()
  const options = {
    scrollbars: {
      visibility: 'auto',
      autoHide: 'move',
      autoHideDelay: 1000,
    },
  }
  const [initialize, osInstance] = useOverlayScrollbars({
    options,
    defer: true,
  })
  const rootRef = useRef(null)
  const [scroller, setScroller] = useState(null)

  useEffect(() => {
    const { current: root } = rootRef
    if (scroller && root) {
      initialize({
        target: root,
        elements: {
          viewport: scroller,
        },
      })
    }
    return () => osInstance()?.destroy()
  }, [scroller, initialize, osInstance])

  useEffect(() => {
    if (selectedCard && selectedCard?._id) {
      fetchCardSessionList(selectedCard._id)
    }
  }, [selectedCard?._id])
  useEffect(() => {
    if (selectedCard?._id && cardSessions.length > 0) {
      cardSessions.forEach((session) => {
        Emitter.subscribeToChannel(
          `operator/${operator._id}/chargePoint/${session.chargePoint.huid}/connector/${session.connectorUid}/sessionStatus/`,
          operator.channelKey,
          emitterUpdateCardSessionStatus,
          {},
        )
        Emitter.subscribeToChannel(
          `operator/${operator._id}/chargePoint/${session.chargePoint.huid}/updateConnector/`,
          operator.channelKey,
          (args) => {
            emitterUpdateCardSessionStatus(args.data)
          },
          {},
        )
      })
    }
  }, [selectedCard?._id, cardSessions])

  const cardsContainerRef = useRef()

  return (
    <div className={Styles.Container} ref={cardsContainerRef}>
      {fetchCardSessionListRequestState === RequestState.LOADING ||
      fetchCardListRequestState === RequestState.LOADING ||
      fetchCardRequestState === RequestState.LOADING ? (
        <div>
          {[...Array(3)].map((e, index) => (
            <div
              style={{
                marginRight: 16,
              }}
              key={`CardSessionListItemSkeleton ${index}`}
            >
              <CardSessionListItemSkeleton key={index} />
            </div>
          ))}
        </div>
      ) : cardSessions.length === 0 ? (
        <div className={Styles.NoRecord}>
          <IFText>{t('CardsPage.EmptySessions')}</IFText>
        </div>
      ) : (
        <div style={{ height: '100%' }}>
          {cardSessions.length < 3 ? (
            cardSessions.map((session, index) => {
              const connector = session.chargePoint.evses
                ?.find((evse) =>
                  evse.connectors?.some(
                    (connector) =>
                      connector.uid?.toString() ===
                      session.connectorUid?.toString(),
                  ),
                )
                ?.connectors?.find(
                  (connector) =>
                    connector.uid?.toString() ===
                    session.connectorUid?.toString(),
                )
              return (
                <div
                  className={Styles.ItemContainer}
                  key={`CardSessionListItem ${index}`}
                >
                  <CardSessionListItem
                    connectorUid={session.connectorUid}
                    connectorType={connector.type}
                    energyConsumption={
                      session.transaction.meterEnd
                        ? (
                            session.transaction.meterEnd -
                            session.transaction.meterStart
                          ).toFixed(2)
                        : 0
                    }
                    startedAt={session.startedAt}
                    stationName={session.chargePoint.station.name}
                    chargePointName={session.chargePoint.name}
                    reservationID={session.reservation?.uid}
                    chargingLevel={handleMaxPower(connector.maxPower)}
                    chargePointId={session.chargePoint._id}
                    stationId={session.chargePoint.station._id}
                    connectorStatus={connector.status}
                    isChargePointOffline={!session.chargePoint.isOnline?.value}
                  />
                </div>
              )
            })
          ) : (
            <div
              ref={rootRef}
              data-overlayscrollbars=""
              style={{
                width: '100%',
                height: '100%',
              }}
            >
              <Virtuoso
                data={cardSessions}
                increaseViewportBy={480}
                itemContent={(index, session) => {
                  const connector = session.chargePoint.evses
                    ?.find((evse) =>
                      evse.connectors?.some(
                        (connector) =>
                          connector.uid?.toString() ===
                          session.connectorUid?.toString(),
                      ),
                    )
                    ?.connectors?.find(
                      (connector) =>
                        connector.uid?.toString() ===
                        session.connectorUid?.toString(),
                    )
                  return (
                    <div
                      className={Styles.ItemContainer}
                      key={`CardSessionListItem ${index}`}
                    >
                      <CardSessionListItem
                        connectorUid={session.connectorUid}
                        connectorType={connector?.type}
                        energyConsumption={
                          session.transaction.meterEnd
                            ? (
                                session.transaction.meterEnd -
                                session.transaction.meterStart
                              ).toFixed(2)
                            : 0
                        }
                        startedAt={session.startedAt}
                        stationName={session.chargePoint.station.name}
                        chargePointName={session.chargePoint.name}
                        reservationID={session.reservation?.uid}
                        chargingLevel={handleMaxPower(connector.maxPower)}
                        chargePointId={session.chargePoint._id}
                        stationId={session.chargePoint.station._id}
                        connectorStatus={connector.status}
                        isChargePointOffline={
                          !session.chargePoint.isOnline?.value
                        }
                      />
                    </div>
                  )
                }}
                scrollerRef={setScroller}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchCardSessionList: (cardId) =>
      dispatch(CardActions.fetchCardSessionList(cardId)),

    emitterUpdateCardSessionStatus: (data) =>
      dispatch(CardActions.emitterUpdateCardSessionStatus(data)),
  }
}

const mapStateToProps = (state) => ({
  fetchCardSessionListRequestState:
    CardSelectors.getFetchCardSessionListRequestState(state),
  cardSessions: CardSelectors.getCardSessions(state),
  selectedCard: CardSelectors.getSelectedCard(state),
  fetchCardListRequestState: CardSelectors.getFetchCardListRequestState(state),
  fetchCardRequestState: CardSelectors.getFetchCardRequestState(state),
  operator: AuthSelectors.getOperator(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(CardSessionsList)
